import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Building2, Users, LogOut, BarChart3, UserPlus, Plus } from 'lucide-react';
import CompaniesManagement from '../components/CompaniesManagement';
import UsersManagement from '../components/UsersManagement';
import SuperAdminHome from '../components/SuperAdminHome';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = ({ currentUser, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const navItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3, path: '/super-admin' },
    { id: 'companies', label: 'Companies', icon: Building2, path: '/super-admin/companies' },
    { id: 'users', label: 'Users', icon: Users, path: '/super-admin/users' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Super Admin Portal</h1>
                <p className="text-xs text-gray-500">Multi-tenant CRM Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium" data-testid="admin-user-name">{currentUser.full_name}</p>
                <p className="text-xs text-gray-500">{currentUser.email}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onLogout}
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen bg-white border-r border-gray-200 p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setActiveTab(item.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === item.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <Routes>
            <Route index element={<SuperAdminHome getAuthHeaders={getAuthHeaders} />} />
            <Route path="companies" element={<CompaniesManagement getAuthHeaders={getAuthHeaders} />} />
            <Route path="users" element={<UsersManagement getAuthHeaders={getAuthHeaders} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
